import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Avatar,
    Divider,
    IconButton,
    Grid,
    Stepper,
    Step,
    StepLabel,
    Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import EventIcon from '@mui/icons-material/Event';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';

import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import ContractPreview from './ContractPreview';

interface ContractModalProps {
    open: boolean;
    onClose: () => void;
    organizationId: string | null;
    organizationName: string | null;
    offer: any;
}

const steps = ['Détails des Parties', 'Lieu & Date', 'Aperçu & Validation'];

const ContractModal: React.FC<ContractModalProps> = ({ open, onClose, organizationId, organizationName, offer }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);

    const [provider, setProvider] = useState(() => {
        const saved = localStorage.getItem('contract_provider_info');
        return saved ? JSON.parse(saved) : {
            gieName: 'Edu-JALM',
            address: 'Adresse du Prestataire',
            representative: 'Nom du Prestataire',
            function: 'Gérant',
            litigationCity: 'Paris'
        };
    });

    const [client, setClient] = useState({
        name: organizationName || '',
        address: '',
        representative: '',
        function: 'Gérant',
        email: ''
    });

    const [contractMeta, setContractMeta] = useState({
        city: 'Paris',
        date: new Date().toLocaleDateString('fr-FR')
    });

    useEffect(() => {
        if (organizationName) {
            setClient(prev => ({ ...prev, name: organizationName }));
        }
    }, [organizationName]);

    // Save provider info
    useEffect(() => {
        localStorage.setItem('contract_provider_info', JSON.stringify(provider));
    }, [provider]);

    const handleGenerate = async () => {
        if (!organizationId || !offer) return;
        setLoading(true);
        try {
            await api.post('/api/contract', {
                organization: organizationId,
                offerId: offer._id,
                provider,
                client,
                meta: contractMeta
            });
            showToast('Contrat généré avec succès', 'success');
            onClose();
        } catch (err) {
            console.error('Error generating contract:', err);
            showToast('Erreur lors de la génération du contrat', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon color="primary" /> Prestataire
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Nom du GIE"
                                    fullWidth
                                    value={provider.gieName}
                                    onChange={(e) => setProvider({ ...provider, gieName: e.target.value })}
                                />
                                <TextField
                                    label="Adresse"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={provider.address}
                                    onChange={(e) => setProvider({ ...provider, address: e.target.value })}
                                />
                                <TextField
                                    label="Représentant"
                                    fullWidth
                                    value={provider.representative}
                                    onChange={(e) => setProvider({ ...provider, representative: e.target.value })}
                                />
                                <TextField
                                    label="Fonction"
                                    fullWidth
                                    value={provider.function}
                                    onChange={(e) => setProvider({ ...provider, function: e.target.value })}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BusinessIcon color="primary" /> Client
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Organisme"
                                    fullWidth
                                    value={client.name}
                                    onChange={(e) => setClient({ ...client, name: e.target.value })}
                                />
                                <TextField
                                    label="Adresse"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={client.address}
                                    onChange={(e) => setClient({ ...client, address: e.target.value })}
                                />
                                <TextField
                                    label="Représentant"
                                    fullWidth
                                    value={client.representative}
                                    onChange={(e) => setClient({ ...client, representative: e.target.value })}
                                />
                                <TextField
                                    label="Fonction"
                                    fullWidth
                                    value={client.function}
                                    onChange={(e) => setClient({ ...client, function: e.target.value })}
                                />
                                <TextField
                                    label="Email Client (pour l'envoi)"
                                    fullWidth
                                    type="email"
                                    value={client.email}
                                    onChange={(e) => setClient({ ...client, email: e.target.value })}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                );
            case 1:
                return (
                    <Grid container spacing={4} justifyContent="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FmdGoodIcon color="primary" /> Lieu de signature
                            </Typography>
                            <TextField
                                label="Ville"
                                fullWidth
                                value={contractMeta.city}
                                onChange={(e) => setContractMeta({ ...contractMeta, city: e.target.value })}
                                sx={{ mb: 3 }}
                            />

                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EventIcon color="primary" /> Date de signature
                            </Typography>
                            <TextField
                                label="Date"
                                fullWidth
                                value={contractMeta.date}
                                onChange={(e) => setContractMeta({ ...contractMeta, date: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                );
            case 2:
                return (
                    <Box sx={{ maxHeight: '60vh', overflowY: 'auto', p: 1, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                        <ContractPreview
                            provider={provider}
                            client={client}
                            offer={offer}
                            meta={contractMeta}
                        />
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={activeStep === 2 ? "lg" : "md"}
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, overflow: 'hidden' }
            }}
        >
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                        <DescriptionIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {activeStep === 2 ? 'Vérification du Contrat' : 'Générer un Contrat'}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {offer?.title} pour {organizationName}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ px: 4, pt: 3 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            <DialogContent sx={{ p: 4 }}>
                {renderStepContent(activeStep)}
            </DialogContent>

            <DialogActions sx={{ p: 3, bgcolor: '#f5f7fa' }}>
                <Button onClick={onClose} sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Annuler
                </Button>
                <Box sx={{ flexGrow: 1 }} />

                {activeStep > 0 && (
                    <Button
                        onClick={handleBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{ mr: 1, fontWeight: 600 }}
                    >
                        Retour
                    </Button>
                )}

                {activeStep < steps.length - 1 ? (
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            px: 4,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: '#3b82f6',
                            '&:hover': { bgcolor: '#2563eb' }
                        }}
                    >
                        Suivant
                    </Button>
                ) : (
                    <Button
                        onClick={handleGenerate}
                        variant="contained"
                        disabled={loading}
                        startIcon={<SaveIcon />}
                        sx={{
                            px: 4,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                            }
                        }}
                    >
                        {loading ? 'Génération...' : 'Confirmer et Créer'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ContractModal;
